import { useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Play, Clock } from 'lucide-react';
import { useTimer } from '../context/TimerContext';

const presetTimes = [
  { label: '1 min', value: 1 }, { label: '2 min', value: 2 },
  { label: '5 min', value: 5 }, { label: '10 min', value: 10 },
  { label: '15 min', value: 15 }, { label: '20 min', value: 20 },
  { label: '25 min', value: 25 }, { label: '30 min', value: 30 },
];

export const TimerPage = () => {
  const { startTimer, openTimer, isOpen, isRunning, formatTime, minutes: runMin, seconds: runSec } = useTimer();
  const [customMin, setCustomMin] = useState(5);
  const [customSec, setCustomSec] = useState(0);

  const handlePreset = (mins) => startTimer(mins, 0);
  const handleCustomStart = () => { if (customMin > 0 || customSec > 0) startTimer(customMin, customSec); };

  return (
    <Layout breadcrumb={[{ label: 'Teach Now', path: '/app/live-mode' }, { label: 'Timer' }]}>
      <div className="max-w-xl mx-auto space-y-5" data-testid="timer-page">
        {/* Current timer status */}
        {isOpen && (
          <Card className="bg-teal-50 border border-teal-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${isRunning ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                <div>
                  <p className="text-sm font-semibold text-indigo-800">Timer {isRunning ? 'Running' : 'Paused'}</p>
                  <p className="text-xs text-teal-600 font-mono">{formatTime(runMin, runSec)}</p>
                </div>
              </div>
              <Button size="sm" onClick={openTimer} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs" data-testid="show-timer-btn">
                Show Timer
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quick presets */}
        <Card className="bg-white border border-slate-200 shadow-soft">
          <CardContent className="p-5">
            <h2 className="text-sm font-heading font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-teal-500" /> Quick Start
            </h2>
            <p className="text-xs text-slate-500 mb-4">Tap a preset to start a floating timer you can use while navigating</p>
            <div className="grid grid-cols-4 gap-2">
              {presetTimes.map(p => (
                <Button
                  key={p.value}
                  variant="outline"
                  onClick={() => handlePreset(p.value)}
                  className="border-slate-200 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-600 rounded-xl h-12 text-sm font-medium"
                  data-testid={`preset-${p.value}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Custom time */}
        <Card className="bg-white border border-slate-200 shadow-soft">
          <CardContent className="p-5">
            <h2 className="text-sm font-heading font-semibold text-slate-800 mb-3">Custom Timer</h2>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-slate-500 mb-1 block">Minutes</label>
                <Input
                  type="number" min="0" max="120"
                  value={customMin}
                  onChange={(e) => setCustomMin(Math.min(120, Math.max(0, parseInt(e.target.value) || 0)))}
                  className="border-slate-200 rounded-xl text-center text-lg font-mono h-12"
                  data-testid="custom-minutes-input"
                />
              </div>
              <span className="text-2xl font-bold text-slate-300 pb-2">:</span>
              <div className="flex-1">
                <label className="text-xs text-slate-500 mb-1 block">Seconds</label>
                <Input
                  type="number" min="0" max="59"
                  value={customSec}
                  onChange={(e) => setCustomSec(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
                  className="border-slate-200 rounded-xl text-center text-lg font-mono h-12"
                  data-testid="custom-seconds-input"
                />
              </div>
              <Button
                onClick={handleCustomStart}
                disabled={customMin === 0 && customSec === 0}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-12 px-6"
                data-testid="start-custom-timer-btn"
              >
                <Play className="w-4 h-4 mr-1.5" /> Start
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
